import { GroupRole } from '@/__generated__/graphql';
import Dialog from '@/components/Dialog';
import { capitalize } from '@/utils';

const ROLE_LABELS: Partial<Record<GroupRole, string>> = {
  [GroupRole.Viewer]: 'Viewer',
  [GroupRole.Member]: 'Member',
  [GroupRole.Admin]: 'Admin',
};

function roleLabel(role: GroupRole): string {
  return ROLE_LABELS[role] || capitalize(role);
}

// Who holds the team's Owner seat today, as far as the client can tell. The
// caller may know the owner is the signed-in user, may know someone else's
// name, or may not be able to resolve either — that third case must never be
// rendered as "You" (ENG-5529).
export type CurrentOwner = { kind: 'viewer' } | { kind: 'other'; name?: string } | { kind: 'none' };

interface TransferOwnershipDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  submitting?: boolean;
  targetMember: { name: string; role: GroupRole } | null;
  currentOwner: CurrentOwner;
}

export function TransferOwnershipDialog({
  open,
  onClose,
  onConfirm,
  submitting,
  targetMember,
  currentOwner,
}: TransferOwnershipDialogProps): JSX.Element {
  const label = roleLabel(targetMember?.role ?? GroupRole.Member);
  const isViewer = targetMember?.role === GroupRole.Viewer;
  const ownerIsViewer = currentOwner.kind === 'viewer';
  const ownerLabel = ownerIsViewer
    ? 'You'
    : currentOwner.kind === 'other'
      ? (currentOwner.name ?? 'The current owner')
      : null;

  return (
    <Dialog
      open={open && !!targetMember}
      title="Transfer Ownership"
      onConfirm={() => onConfirm()}
      onCancel={onClose}
      confirmLabel="Transfer Ownership"
      cancelLabel="Cancel"
      submitting={submitting}
      confirmButtonProps={{ color: 'red' }}
    >
      <p>
        {targetMember?.name} will become the team Owner with full admin rights, including the
        ability to manage billing and team members.
      </p>
      {ownerLabel && (
        <p>
          <span>{`${ownerLabel} will become a ${label}.`}</span>
          {isViewer && (
            <span>
              {` ${ownerIsViewer ? 'You' : 'They'} will no longer be able to create expert requests or book consultations.`}
            </span>
          )}
        </p>
      )}
    </Dialog>
  );
}
